import { Avatar, Box, Button, ButtonBase, Stack, Typography } from "@material-ui/core";
import { Call, OpenInNew, WhatsApp } from "@material-ui/icons";
import { useTranslation } from "react-i18next";
import {
  getLocalPhoneNumberIfIsraeliNumber,
  toInternationalFormat
} from "utils/Haim/phoneNumbersUtils";

type ListingPublisherCardProps = {
  name?: string;
  phoneNumber?: string;
  avatarUrl?: string;
  agencyName?: string;
  whatsAppMessage?: string;
  externalListingUrl?: string;
  externalListingSourceLabel?: string;
  /**
   * Opens this person's contact card. When given, the NAME AND AVATAR become the
   * control — clicking a person's name to see that person is the obvious gesture,
   * and it needs no label taking up a row of its own.
   *
   * Omitted for anyone who is not a contact (a scraped seller, or an owner
   * recorded on the listing without a CRM card), so the name is plain text and
   * nothing promises a card that does not exist.
   */
  onNameClick?: () => void;
};

export function ListingPublisherCard({
  name,
  phoneNumber,
  avatarUrl,
  agencyName,
  whatsAppMessage,
  externalListingUrl,
  externalListingSourceLabel,
  onNameClick
}: ListingPublisherCardProps): JSX.Element | null {
  const { t: translate } = useTranslation();

  if (!name && !phoneNumber && !externalListingUrl) return null;

  const displayPhone = phoneNumber
    ? getLocalPhoneNumberIfIsraeliNumber(phoneNumber, false) ?? phoneNumber
    : undefined;
  const telPhone = phoneNumber ? toInternationalFormat(phoneNumber) ?? phoneNumber : undefined;
  const whatsappDigits = telPhone ? telPhone.replace(/\D/g, "") : undefined;
  const whatsAppHref = whatsappDigits
    ? `https://wa.me/${whatsappDigits}${
        whatsAppMessage ? `?text=${encodeURIComponent(whatsAppMessage)}` : ""
      }`
    : undefined;

  return (
    <Box
      sx={{
        p: 2,
        borderRadius: 1,
        border: 1,
        borderColor: "divider"
      }}
    >
      <Stack
        direction={{ xs: "column", sm: "row" }}
        alignItems={{ xs: "stretch", sm: "center" }}
        spacing={2}
      >
        <Stack
          // `ButtonBase` when it is a control, a plain Stack when it is not —
          // rather than one element with a conditional handler. A div that is
          // sometimes clickable is not keyboard reachable and announces nothing;
          // ButtonBase carries the role, focus ring and Enter/Space for free.
          component={onNameClick ? ButtonBase : "div"}
          {...(onNameClick
            ? {
                onClick: onNameClick,
                // The card can sit inside a clickable row (the board), so the
                // gesture must not also open whatever is behind it.
                onMouseDown: (event: React.MouseEvent) => event.stopPropagation()
              }
            : {})}
          direction="row"
          alignItems="center"
          spacing={2}
          sx={{
            flex: 1,
            minWidth: 0,
            ...(onNameClick
              ? {
                  // Negative margin keeps the hit area generous without moving
                  // the avatar off the alignment it shares with the non-clickable
                  // variant.
                  p: 0.5,
                  m: -0.5,
                  borderRadius: 1,
                  justifyContent: "flex-start",
                  textAlign: "start",
                  "&:hover": { bgcolor: "action.hover" },
                  "&:hover .publisher-name": { textDecoration: "underline" }
                }
              : {})
          }}
        >
          <Avatar src={avatarUrl} sx={{ width: 48, height: 48 }}>
            {name?.[0]?.toUpperCase()}
          </Avatar>
          <Stack sx={{ minWidth: 0 }}>
            {name && (
              <Typography
                className="publisher-name"
                variant="subtitle1"
                noWrap
                sx={onNameClick ? { color: "primary.main" } : undefined}
              >
                {name}
              </Typography>
            )}
            {agencyName && (
              <Typography variant="body2" color="text.secondary" noWrap>
                {agencyName}
              </Typography>
            )}
          </Stack>
        </Stack>
        <Stack direction="row" spacing={1} flexWrap="wrap">
          {displayPhone && (
            <Button
              href={`tel:${telPhone}`}
              startIcon={<Call />}
              variant="contained"
              size="small"
              dir="ltr"
              sx={{
                bgcolor: "info.lighter",
                color: "info.darker",
                boxShadow: "none",
                "&:hover": { bgcolor: "info.light", boxShadow: "none" }
              }}
            >
              {displayPhone}
            </Button>
          )}
          {whatsAppHref && (
            <Button
              href={whatsAppHref}
              target="_blank"
              rel="noopener noreferrer"
              startIcon={<WhatsApp />}
              variant="contained"
              size="small"
              sx={{
                bgcolor: "success.lighter",
                color: "success.darker",
                boxShadow: "none",
                "&:hover": { bgcolor: "success.light", boxShadow: "none" }
              }}
            >
              WhatsApp
            </Button>
          )}
          {externalListingUrl && (
            <Button
              component="a"
              href={externalListingUrl}
              target="_blank"
              rel="noopener noreferrer"
              startIcon={<OpenInNew />}
              variant="contained"
              size="small"
              sx={{
                bgcolor: "warning.lighter",
                color: "warning.darker",
                boxShadow: "none",
                "&:hover": { bgcolor: "warning.light", boxShadow: "none" }
              }}
            >
              {externalListingSourceLabel
                ? translate("listingPreview.viewOnSource", { source: externalListingSourceLabel })
                : translate("listingPreview.openSource")}
            </Button>
          )}
        </Stack>
      </Stack>
    </Box>
  );
}
